import { QuestionIcon, XIcon } from '@phosphor-icons/react'
import * as Dialog from '@radix-ui/react-dialog'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import { createFormHook, createFormHookContexts, useSelector } from '@tanstack/react-form'
import classNames from 'classnames'
import { useId, useRef, useState } from 'react'
import { match } from 'ts-pattern'
import { DefaultButton } from '../../../components/buttons/default-button.tsx'
import { GhostButton } from '../../../components/buttons/ghost-button.tsx'
import { Disclosure } from '../../../components/disclosure.tsx'
import * as footerStyles from '../../../components/drawers/floating-footer.css.ts'
import { FloatingFooter } from '../../../components/drawers/floating-footer.tsx'
import { RadioGroup, RadioItem } from '../../../components/input/radio.tsx'
import { TextInput } from '../../../components/input/text-input.tsx'
import { useShowMutationError } from '../../../hooks/use-show-mutation-error.tsx'
import { useToastService } from '../../../services/toast-service.tsx'
import {
  useGetIsPostIntegrationMutating,
  usePostIntegration,
  type PostIntegrationVariables,
} from '../../../utils/api-client/api-hooks.ts'
import { parsePositiveInteger } from '../../../utils/text-utils.ts'
import { DocumentationDrawer } from './documentation-drawer.tsx'
import * as styles from './integration-drawer.css.ts'
import { FormErrorBanner, IntegrationFieldInput, getFieldFormatError } from './integration-field-input.tsx'
import { CatalogBrandIcon } from './integration-icon.tsx'
import type { CatalogEntry, CreatableIntegrationType, IntegrationField } from './integration-instance-utils.ts'
import {
  ADVANCED_SECTION_SUMMARY,
  getHasWebhookHandoff,
  getIsFieldRequired,
  getIsSonarCloudUrl,
  getProviderLabel,
  getToolFields,
  parseWorkItemId,
  reportRoleRefusal,
  toCheckmarxRegion,
} from './integration-instance-utils.ts'
import {
  NAME_FIELD_KEY,
  getFieldErrorText,
  getIntegrationSaveError,
  getIsConflictReportedOnField,
  getIsFieldAnchored,
  type IntegrationSaveError,
} from './integration-validation.ts'
import {
  getWebhookAuthMethod,
  getWebhookUrl,
  type WebhookAuthChoice,
  type WebhookHandoffSubject,
} from './integration-webhook.ts'
import { preventEscapeWhile } from './prevent-escape.ts'
import { returnFocusOnClose } from './return-focus.ts'
import { WebhookHandoff } from './webhook-handoff.tsx'

// #region Types

type WebhookAuthMethod = WebhookAuthChoice

/**
 * The instance the create returned, kept so the handoff step can name it and show its webhook URL.
 * `subject` carries the narrowed webhook tool and the one fact its steps turn on, read off the
 * submitted form rather than the response — a webhook credential is write-only, so no response says
 * what was stored. It is built here rather than re-derived by the handoff because only the success
 * handler has proved this instance is a webhook tool at all.
 */
type SavedInstance = {
  name: string
  webhookUrl: string
  subject: WebhookHandoffSubject
}

// The form is a flat name + a per-tool value bag + the AppScan webhook-auth method, plus the step the
// drawer is on and the last failure. Field state lives in the TanStack Form store (not in a component
// that renders Dialog.Content) so typing never re-renders Dialog.Content — that would re-run Radix's
// FocusScope ref callback and exceed React's update-depth limit. The step and the failure live there
// too because the footer owns the mutation and the body renders what it produces.
type SetupFormValues = {
  name: string
  values: Record<string, string>
  webhookMethod: WebhookAuthMethod
  step: 'form' | 'handoff'
  savedInstance: SavedInstance | undefined
  saveError: IntegrationSaveError | undefined
}

type SetupDrawerProps = {
  entry: CatalogEntry
  onClose: () => unknown
  opener?: HTMLElement
}

// #endregion

// #region Form hook

const { fieldContext, formContext } = createFormHookContexts()

const { useAppForm, useTypedAppFormContext } = createFormHook({
  fieldComponents: {},
  formComponents: {},
  fieldContext,
  formContext,
})

const FORM_DEFAULT_VALUES: SetupFormValues = {
  name: '',
  values: {},
  webhookMethod: 'secret',
  step: 'form',
  savedInstance: undefined,
  saveError: undefined,
}

const useSetupForm = (defaultValueOverrides: Partial<SetupFormValues> = {}) =>
  useAppForm({ defaultValues: { ...FORM_DEFAULT_VALUES, ...defaultValueOverrides } })

const useFormContext = () => useTypedAppFormContext({ defaultValues: FORM_DEFAULT_VALUES })

// #endregion

// #region Helpers

// Seeds any field that declares a default.
const initialValuesFor = (fields: IntegrationField[]): Record<string, string> =>
  Object.fromEntries(fields.map(field => [field.key, field.defaultValue ?? '']))

/** The chooser hands back a generic `string`; this is the one place that narrows it. */
const toWebhookAuthMethod = (value: string): WebhookAuthMethod => (value === 'basic' ? 'basic' : 'secret')

/** An optional field is omitted from the request when blank, rather than sent as an empty string. */
const omitIfBlank = (value: string | undefined): string | undefined => (value ? value : undefined)

// AppScan submits exactly one webhook-auth method — the platform rejects both or neither with a 400.
const buildCreateBody = (type: CreatableIntegrationType, form: SetupFormValues): PostIntegrationVariables['body'] => {
  const { webhookMethod } = form
  const name = form.name.trim()
  // A pasted key routinely carries a trailing space or newline, and sending it verbatim fails auth
  // with nothing to point at. No credential or URL has meaningful surrounding whitespace.
  const values = Object.fromEntries(Object.entries(form.values).map(([key, value]) => [key, value.trim()]))
  return match(type)
    .with('datadog', tool => ({
      type: tool,
      name,
      base_uri: values.base_uri ?? '',
      api_key: values.api_key ?? '',
      application_key: values.application_key ?? '',
    }))
    .with('arnica', tool => ({
      type: tool,
      name,
      base_uri: values.base_uri ?? '',
      api_key: values.api_key ?? '',
    }))
    .with('sonar', tool => {
      const isSonarCloud = getIsSonarCloudUrl(values.base_uri)
      return {
        type: tool,
        name,
        base_uri: values.base_uri ?? '',
        token: values.token ?? '',
        organization: isSonarCloud ? values.organization : undefined,
        github_app_id: isSonarCloud ? undefined : parsePositiveInteger(values.github_app_id),
        webhook_secret: omitIfBlank(values.webhook_secret),
      }
    })
    .with('polaris', tool => ({ type: tool, name, base_uri: values.base_uri ?? '', api_token: values.api_token ?? '' }))
    .with('black-duck-hub', tool => ({
      type: tool,
      name,
      base_uri: values.base_uri ?? '',
      api_token: values.api_token ?? '',
    }))
    .with('checkmarx-enterprise', tool => ({
      type: tool,
      name,
      base_uri: values.base_uri ?? '',
      username: values.username ?? '',
      password: values.password ?? '',
      client_id: values.client_id ?? '',
      client_secret: values.client_secret ?? '',
    }))
    .with('veracode', tool => ({
      type: tool,
      name,
      base_uri: values.base_uri ?? '',
      api_key_id: values.api_key_id ?? '',
      api_key_secret: values.api_key_secret ?? '',
      signing_target: omitIfBlank(values.signing_target),
    }))
    .with('checkmarx', tool => ({
      type: tool,
      name,
      region: toCheckmarxRegion(values.region),
      tenant: values.tenant ?? '',
      api_key: values.api_key ?? '',
    }))
    .with('appscan', tool => ({
      type: tool,
      name,
      base_uri: values.base_uri ?? '',
      api_key_id: values.api_key_id ?? '',
      api_key_secret: values.api_key_secret ?? '',
      // The method names which credential the platform will accept and rejects the other one's, so
      // it travels with the credential rather than being inferred from which field is filled.
      // Submit is held until one method is complete, so there is always exactly one to name.
      webhook_auth_method: getWebhookAuthMethod(webhookMethod),
      webhook_secret: webhookMethod === 'secret' ? values.webhook_secret : undefined,
      webhook_user: webhookMethod === 'basic' ? values.webhook_user : undefined,
      webhook_password: webhookMethod === 'basic' ? values.webhook_password : undefined,
    }))
    .with('gitlab', provider => ({
      type: provider,
      name,
      base_uri: values.base_uri ?? '',
      token: values.token ?? '',
      // Omitted rather than sent empty: the platform refuses a blank secret, because a connection
      // that appears to verify deliveries and does not is worse than one that plainly does not.
      webhook_secret: omitIfBlank(values.webhook_secret),
    }))
    .with('bitbucket', provider => ({
      type: provider,
      name,
      username: values.username ?? '',
      email_address: values.email_address ?? '',
      api_token: values.api_token ?? '',
    }))
    .with('azure', provider => ({
      type: provider,
      name,
      base_uri: values.base_uri ?? '',
      organization: values.organization ?? '',
      token: values.token ?? '',
      // A work item ID is digits in a text field, so an unparseable entry is dropped rather than
      // sent as NaN. The field is optional, and the form's own validation is what should catch it.
      work_item_id: parseWorkItemId(values.work_item_id),
      commit_message_prefix: omitIfBlank(values.commit_message_prefix),
      webhook_password: omitIfBlank(values.webhook_password),
    }))
    .with('github', provider => ({
      type: provider,
      name,
      base_uri: values.base_uri ?? '',
      app_id: values.app_id ?? '',
      app_name: values.app_name ?? '',
      private_key: values.private_key ?? '',
      webhook_secret: values.webhook_secret ?? '',
    }))
    .exhaustive()
}

// #endregion

export const SetupDrawer: React.FC<SetupDrawerProps> = ({ entry, onClose, opener }) => {
  const tool = entry.addableType
  const form = useSetupForm({ values: initialValuesFor(tool ? getToolFields({ tool, baseUri: undefined }) : []) })
  const drawerContentRef = useRef<HTMLDivElement>(null)
  const step = useSelector(form.store, state => state.values.step)
  const savedInstance = useSelector(form.store, state => state.values.savedInstance)
  // Scopes the create to this drawer, so a create still running from a Setup drawer that has since
  // closed does not block Escape in this one.
  const callerId = useId()
  const getIsCreating = useGetIsPostIntegrationMutating({ callerId })

  return (
    <Dialog.Root open modal={false} onOpenChange={open => (!open ? onClose() : undefined)}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <form.AppForm>
          <Dialog.Content
            ref={drawerContentRef}
            className={classNames(styles.content, footerStyles.drawerContentPaddingAdjustment)}
            data-has-floating-footer={true}
            onInteractOutside={event => event.preventDefault()}
            onCloseAutoFocus={returnFocusOnClose(opener)}
            // Closing mid-create discards the handoff step, which for Sonar and AppScan is the only
            // place the webhook URL is offered at create time.
            onEscapeKeyDown={preventEscapeWhile(getIsCreating)}
          >
            <div className={styles.header}>
              <Dialog.Title className={styles.titleRow}>
                <span className={styles.title}>Integrate with</span>
                <CatalogBrandIcon icon={entry.icon} />
                <span className={styles.title}>{entry.label}</span>
              </Dialog.Title>
              <Dialog.Close asChild>
                <button type="button" className={styles.closeButton} aria-label="Close">
                  <XIcon size={24} weight="regular" />
                </button>
              </Dialog.Close>
            </div>

            {step === 'handoff' && savedInstance ? (
              <WebhookHandoff
                toolLabel={entry.label}
                instanceName={savedInstance.name}
                webhookUrl={savedInstance.webhookUrl}
                subject={savedInstance.subject}
              />
            ) : (
              <SetupFields tool={tool} />
            )}

            <VisuallyHidden>
              <Dialog.Description>Enter your {entry.label} credentials to connect a new instance.</Dialog.Description>
            </VisuallyHidden>
          </Dialog.Content>

          <FloatingFooter drawerContentRef={drawerContentRef} width="standard">
            <SetupFooter entry={entry} callerId={callerId} onClose={onClose} />
          </FloatingFooter>
        </form.AppForm>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

// #region Field leaves

type SetupFieldsProps = { tool: CreatableIntegrationType | undefined }

const SetupFields: React.FC<SetupFieldsProps> = ({ tool }) => {
  const form = useFormContext()
  const name = useSelector(form.store, state => state.values.name)
  const values = useSelector(form.store, state => state.values.values)
  const saveError = useSelector(form.store, state => state.values.saveError)
  const fields = tool ? getToolFields({ tool, baseUri: values.base_uri }) : []

  const setValue = (key: string, value: string) =>
    form.setFieldValue('values', previous => ({ ...previous, [key]: value }))

  const mainFields = fields.filter(field => !field.isAdvanced)
  const advancedFields = fields.filter(field => field.isAdvanced)
  const advancedSummary = tool ? ADVANCED_SECTION_SUMMARY[tool] : undefined
  // Nothing is stored on a create, so the only thing that opens this section by itself is a server
  // error anchored inside it.
  const hasAdvancedError = advancedFields.some(field => getIsFieldAnchored(saveError, field.key))
  // A malformed value disables "Test and connect", so the section holding it stays open: collapsed,
  // it would leave a disabled button with nothing on screen explaining why.
  const hasAdvancedFormatError = advancedFields.some(
    field => getFieldFormatError(field, values[field.key]) !== undefined
  )

  const renderField = (field: IntegrationField) => (
    <IntegrationFieldInput
      key={field.key}
      field={field}
      id={`integration-${field.key}`}
      value={values[field.key] ?? ''}
      onChange={value => setValue(field.key, value)}
      errorText={getFieldErrorText(saveError, field.key)}
      isError={getIsFieldAnchored(saveError, field.key)}
    />
  )

  return (
    <div className={styles.section}>
      <h3 className={styles.sectionHeading}>Connect your instance</h3>
      {saveError?.formMessage && <FormErrorBanner message={saveError.formMessage} />}
      <div className={styles.fields}>
        {/* Required by the create contract (`minLength: 1`), and it is what tells two instances of
            the same tool apart everywhere they are listed. */}
        <TextInput
          id="integration-name"
          label="Name"
          isRequired
          value={name}
          onChange={value => form.setFieldValue('name', value)}
          placeholder="Name this integration"
          helperText="How this instance is identified across Pixee."
          errorText={getFieldErrorText(saveError, NAME_FIELD_KEY)}
          isError={getIsFieldAnchored(saveError, NAME_FIELD_KEY)}
        />
        {mainFields.map(renderField)}
        {tool === 'appscan' && <SetupWebhookFieldset />}
        {advancedSummary && advancedFields.length > 0 && (
          <Disclosure summary={advancedSummary} defaultOpen={hasAdvancedError} isForcedOpen={hasAdvancedFormatError}>
            {advancedFields.map(renderField)}
          </Disclosure>
        )}
      </div>
    </div>
  )
}

const WEBHOOK_AUTH_DESCRIPTION_ID = 'webhook-auth-description'

const SetupWebhookFieldset: React.FC = () => {
  const form = useFormContext()
  const webhookMethod = useSelector(form.store, state => state.values.webhookMethod)
  const values = useSelector(form.store, state => state.values.values)
  const setValue = (key: string, value: string) =>
    form.setFieldValue('values', previous => ({ ...previous, [key]: value }))

  return (
    <fieldset className={styles.webhookAuthFieldset}>
      <legend className={styles.sectionHeading}>Webhook authentication</legend>
      <p id={WEBHOOK_AUTH_DESCRIPTION_ID} className={styles.webhookAuthLegend}>
        How AppScan proves to Pixee that a webhook call is genuine. You will configure the same method in AppScan.
      </p>
      <RadioGroup
        className={styles.webhookAuthOptions}
        aria-describedby={WEBHOOK_AUTH_DESCRIPTION_ID}
        value={webhookMethod}
        onValueChange={value => {
          const method = toWebhookAuthMethod(value)
          form.setFieldValue('webhookMethod', method)
          // Exactly one method is submitted, so the other's values are dead once the user switches.
          // Cleared rather than left behind, so nothing the user typed and abandoned can be read back
          // — the handoff step's "was a secret set?" question reads this same bag.
          form.setFieldValue('values', previous => ({
            ...previous,
            webhook_secret: method === 'secret' ? (previous.webhook_secret ?? '') : '',
            webhook_user: method === 'basic' ? (previous.webhook_user ?? '') : '',
            webhook_password: method === 'basic' ? (previous.webhook_password ?? '') : '',
          }))
        }}
      >
        <RadioItem id="webhook-method-secret" value="secret" label="Shared secret" />
        <RadioItem id="webhook-method-basic" value="basic" label="Basic auth" />
      </RadioGroup>
      {match(webhookMethod)
        .with('secret', () => (
          <TextInput
            id="integration-webhook_secret"
            label="Webhook secret"
            type="password"
            isRequired
            value={values.webhook_secret}
            onChange={value => setValue('webhook_secret', value)}
          />
        ))
        .with('basic', () => (
          <>
            <TextInput
              id="integration-webhook_user"
              label="Webhook username"
              isRequired
              value={values.webhook_user}
              onChange={value => setValue('webhook_user', value)}
            />
            <TextInput
              id="integration-webhook_password"
              label="Webhook password"
              type="password"
              isRequired
              value={values.webhook_password}
              onChange={value => setValue('webhook_password', value)}
            />
          </>
        ))
        .exhaustive()}
    </fieldset>
  )
}

// #endregion

// #region Footer leaf

type SetupFooterProps = { entry: CatalogEntry; callerId: string; onClose: () => unknown }

const SetupFooter: React.FC<SetupFooterProps> = ({ entry, callerId, onClose }) => {
  const form = useFormContext()
  const tool = entry.addableType
  const name = useSelector(form.store, state => state.values.name)
  const values = useSelector(form.store, state => state.values.values)
  const fields = tool ? getToolFields({ tool, baseUri: values.base_uri }) : []
  const webhookMethod = useSelector(form.store, state => state.values.webhookMethod)
  const step = useSelector(form.store, state => state.values.step)

  const { addToast } = useToastService()
  const [docsOpener, setDocsOpener] = useState<HTMLElement>()

  // No `409` override: `POST /api/v1/integrations` answers a duplicate name and an already-connected
  // GitHub App under the same status, so one keyed on the status alone asserted the name was at
  // fault whichever it was — and contradicted the App id field that now says otherwise. A conflict
  // anchored to a field is not toasted at all; anything else carries the platform's own words.
  const showConnectError = useShowMutationError({ prefix: 'Failed to connect integration' })
  const postIntegration = usePostIntegration({
    callerId,
    onSuccess: data => {
      addToast({ message: <>{entry.label} integration added.</>, variant: 'success' })
      const webhookTool = tool !== undefined && getHasWebhookHandoff(tool) ? tool : undefined
      const webhookUrl = webhookTool === undefined ? undefined : getWebhookUrl(data)
      // The instance is saved either way. Without a URL to hand over there is nothing for the step
      // to show, so the drawer closes as it does for a pull-only tool rather than showing an empty
      // step. The contract requires the link on both webhook tools, so this is the defensive arm
      // rather than the expected one — the create response is never parsed against that contract.
      if (webhookTool === undefined || webhookUrl === undefined) {
        onClose()
        return
      }
      const subject: WebhookHandoffSubject = match(webhookTool)
        .with('sonar', type => ({
          type,
          hasWebhookSecret: (form.state.values.values.webhook_secret ?? '').trim() !== '',
        }))
        .with('appscan', type => ({ type, webhookAuthChoice: form.state.values.webhookMethod }))
        .exhaustive()
      form.setFieldValue('savedInstance', { name: form.state.values.name.trim(), webhookUrl, subject })
      form.setFieldValue('saveError', undefined)
      form.setFieldValue('step', 'handoff')
    },
    onError: (...args) => {
      const [error] = args
      if (reportRoleRefusal({ error, message: <>You do not have permission to add integrations.</>, addToast })) {
        return
      }
      // Decides which field the drawer marks, so the user is pointed at the input they can act on
      // rather than at the form as a whole.
      const saveError = getIntegrationSaveError({
        error,
        fields,
        values: form.state.values.values,
        toolLabel: tool ? getProviderLabel(tool) : entry.label,
        operation: 'create',
      })
      form.setFieldValue('saveError', saveError)
      // A conflict the field already explains is not repeated in a toast — two reports of one
      // refusal disagree as soon as they name different causes, which is how an already-connected
      // App came to be reported as a duplicate name.
      if (getIsConflictReportedOnField(error, saveError)) {
        return
      }
      showConnectError(...args)
    },
  })

  if (step === 'handoff') {
    return (
      <div className={styles.footerActions}>
        <DefaultButton buttonType="primary" onClick={onClose}>
          Done
        </DefaultButton>
      </div>
    )
  }

  const isAppScan = tool === 'appscan'
  const areRequiredFieldsFilled = fields.every(
    field => !getIsFieldRequired(field) || (values[field.key] ?? '').trim() !== ''
  )
  // Two different failures, held the same way: a malformed URL fails the request schema, and a work
  // item ID that will not parse is dropped from the body instead, which creates the connection
  // without the reference the user thought they had named.
  const areFieldFormatsValid = fields.every(field => getFieldFormatError(field, values[field.key]) === undefined)
  const isWebhookFilled = match(webhookMethod)
    .with('secret', () => (values.webhook_secret ?? '').trim() !== '')
    .with('basic', () => (values.webhook_user ?? '').trim() !== '' && (values.webhook_password ?? '').trim() !== '')
    .exhaustive()
  const isSubmitDisabled =
    tool === undefined ||
    name.trim() === '' ||
    !areRequiredFieldsFilled ||
    !areFieldFormatsValid ||
    (isAppScan && !isWebhookFilled)

  const handleSubmit = () => {
    if (tool === undefined) {
      return
    }
    form.setFieldValue('saveError', undefined)
    postIntegration.mutate({ body: buildCreateBody(tool, form.state.values) })
  }

  return (
    <>
      <button
        type="button"
        className={styles.docsTrigger}
        onClick={event => {
          const trigger = event.currentTarget
          setDocsOpener(prev => (prev ? undefined : trigger))
        }}
      >
        <span className={styles.docsTriggerIcon}>
          <QuestionIcon size={20} weight="regular" />
        </span>
        View documentation
      </button>
      <div className={styles.footerActions}>
        {/* Cancelling mid-create would leave the drawer guessing whether the instance was made, so
            the connect in flight owns the footer until it settles. */}
        <GhostButton type="secondary" state={postIntegration.isPending ? 'disabled' : 'default'} onClick={onClose}>
          Cancel
        </GhostButton>
        <DefaultButton
          buttonType="primary"
          state={postIntegration.isPending ? 'loading' : isSubmitDisabled ? 'disabled' : 'default'}
          onClick={handleSubmit}
          loadingLabel="Testing"
        >
          Test and connect
        </DefaultButton>
      </div>
      {docsOpener && (
        <DocumentationDrawer
          entry={entry}
          placement="beside"
          opener={docsOpener}
          onClose={() => setDocsOpener(undefined)}
        />
      )}
    </>
  )
}

// #endregion
